import {matterService} from "../services/matterService";
import {
  contactService,
  bookingsService,
  documentService,
  docuformsService,
  notesService,
  staffService
} from "@/modules/common/services";
import { pollNoteUntilSummaryReady, fetchNoteV3 } from '@/modules/note/services/noteSummaryPollingService';
import { NOTE_SUMMARY_STATUS } from '@/modules/note/constants';

import {
  paymentsService,
  paymentsStatusesService,
  estimateService
} from "@/modules/payments/services";
import conversationService from "@/modules/matters/services/conversationService";
import _ from 'lodash'
import Vue from 'vue'
import {SMS_CHANNEL_UID, EMAIL_CHANNEL_UID} from "@/modules/account/constants/channels.js";
import {staffNameByUid} from "@/modules/account/utils/staffUtils";

const defaultFilterParams = {
  page: 0,
  per_page: 20
}

const getDefaultState = () =>{
  return {
    matter: null,
    docuform: null,
    loadingMatter: true,
    summary: { loading: true },
    currContact: null,
    isClientOptOutSms: false,
    preferredLanguage: null,
    isSmsAvailable: false,
    contactFields: [],
    activeMatterTab: sessionStorage.getItem('activeMatterTab'),
    bookings: {
      past: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      },
      upcoming: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      }
    },
    conversation: {
      filterBy: {...defaultFilterParams},
      completed: false,
      error: false,
      latest_update: 0,
      messages: [],
      channel: EMAIL_CHANNEL_UID,
    },
    documents:{
      filterBy: {...defaultFilterParams},
      completed: false,
      docs: [],
    },
    docuforms: {
      all: {
        filterBy: {...defaultFilterParams},
        completed: false,
        error: false,
        items: []
      },
      approved: {
        filterBy: {...defaultFilterParams},
        completed: false,
        items: []
      },
      client: {
        filterBy: {...defaultFilterParams},
        completed: false,
        items: []
      },
      business: {
        filterBy: {...defaultFilterParams},
        completed: false,
        items: []
      },
      internal: {
        filterBy: {...defaultFilterParams},
        completed: false,
        items: []
      }
    },
    payments: {
      filterBy: {...defaultFilterParams},
      summary: { loading: false, data: null },
      pendingInvoices: [],
      openPaymentRequests: { totalAmount: 0, currency: 'USD' },
      overdueAndDue: {
        filterBy: {...defaultFilterParams},
        completed: false,
        loading: false,
        list: [],
      },
      notYetDue: {
        filterBy: {...defaultFilterParams},
        completed: false,
        loading: false,
        list: [],
      },
      received: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      },
      scheduledPayments: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      },
      estimates: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      }
    },
    notes: {
      filterBy: {...defaultFilterParams},
      completed: false,
      notes: [],
    },
    activePollingNoteUid: null,
    followUp: null,
    bookingSummary: {
      noShows: 0,
      last: null,
      upcoming: null
    },
    collaborators: [],
    collaboratorsPromise: null,
    matterSummaryPromise: null,
    shouldReloadMatter: false,
    newRecommendationData: false,
    messageDialogOptions: {
      showSmartReply: false,
      showCustomInstructions: false,
    },
  }
};

const getters = {
  matter: (state) => state.matter,
  reminder: (state) => state.matter && state.matter.reminder,
  summaryLoading: state => state.summary.loading,
  recentNote: (state, _getters, rootState) => {
    if (!state.summary.recent || !state.summary.recent.note) return

    const note = state.summary.recent.note
    const staffMembers = rootState.accountModule && rootState.accountModule.staffMembers
    return {
      content: note.content,
      uid: note.uid,
      title: note.title,
      staffName: staffNameByUid(note.staff_uid, staffMembers),
      isUpdated: note.created_at !== note.updated_at,
      createdAt: note.created_at,
      // v1 summary returns a localized updated_at_h; v3 returns ISO updated_at.
      // TODO: (VCITA2-14470 — rollout.clients.new_notes cleanup): read v3 updated_at only.
      updatedAt: note.updated_at_h ?? note.updated_at,
      summary: note.summary,
      summaryStatus: note.summary_status,
      // Server-computed capability (v3); gates the Remove action. Legacy v1 sends no
      // capabilities → default true (ungated). `??` preserves an explicit v3 false.
      canDelete: note.capabilities?.delete ?? true,
      // Flat v3 entity fields, with a fallback to the legacy v1 nested shape during rollout.
      // TODO: (VCITA2-14470 — rollout.clients.new_notes cleanup): drop the fallback, read v3 flat only.
      entityUid: note.entity_uid ?? note.entity?.uid ?? null,
      entityType: note.entity_type ?? note.entity?.type ?? null,
      entityTitle: note.entity_title ?? note.entity?.title ?? null,
    }
  },
  pendingSummaryNoteUids: (state) => (excludeUid) => state.notes.notes
    .filter(n => n.uid !== excludeUid && n.summary_status === NOTE_SUMMARY_STATUS.inProgress)
    .map(n => n.uid),
  activeMatterTab: state => state.activeMatterTab,
  pastBookings: (state)  => state.bookings.past.list,
  pastBookingsPage: (state)  => state.bookings.past.filterBy.page,
  pastBookingsCompleted: (state)  => state.bookings.past.completed,
  upcomingBookings: (state) => state.bookings.upcoming.list,
  upcomingBookingsPage: (state)  => state.bookings.upcoming.filterBy.page,
  upcomingBookingsCompleted: (state)  => state.bookings.upcoming.completed,
  isClientOptOutSms: (state) => state.isClientOptOutSms,
  preferredLanguage: (state) => state.preferredLanguage,
  currContact: (state) => state.currContact,
  isSmsAvailable: (state) => {
    return !!state.currContact?.notifications_phone?.value
        && state.currContact?.is_notifications_phone_mobile
        && !state.isClientOptOutSms},
  contactFields: state => state.contactFields,
  channel: state => state.conversation.channel,
  messages: state => state.conversation.messages,
  messagesPage: (state)  => state.conversation.filterBy.page,
  documents: state => state.documents.docs,
  documentsPage: (state)  => state.documents.filterBy.page,
  paymentsSummary: state  => state.payments.summary.data,
  paymentsSummaryLoading: state  => state.payments.summary.loading,
  hasPendingInvoices: state => Boolean(state.payments.pendingInvoices?.length),
  openPaymentRequests: state => state.payments.openPaymentRequests,
  overdueAndDuePayments: (state) => {
    return state.payments.overdueAndDue.list
      .map(item => _.pick(item, ['uid', 'title_h', 'amount_pending', 'amount_to_pay_h', 'state_summary', 'staff', 'payable_type', 'payable_uid']))
      .map(item => ({
        uid: item.uid,
        title: item.title_h,
        price: item.amount_to_pay_h,
        amount_pending: parseFloat(item.amount_pending),
        status: item.state_summary && item.state_summary.state_h.toLocaleLowerCase(),
        payable_type: item.payable_type,
        payable_uid: item.payable_uid,
        staff: item.staff,
      }))
  },
  overdueAndDuePaymentsPage: (state)  => state.payments.overdueAndDue.filterBy.page,
  overdueAndDuePaymentsCompleted: (state)  => state.payments.overdueAndDue.completed,
  receivedPayments: (state) => {
    return state.payments.received.list
    .map(item => _.pick(item, ['uid', 'title_h', 'amount_h', 'state_summary', 'staff', 'paid_at_h', 'amount_refunded_h','amount_paid_after_refund_h']))
      .map(item => ({
        uid: item.uid,
        title: item.title_h,
        price: item.amount_h,
        status: item.state_summary && item.state_summary.state_h.toLocaleLowerCase(),
        staff: item.staff,
        paid_at_h: item.paid_at_h,
        amount_refunded_h: item.amount_refunded_h,
        amountPaidAfterRefund: item.amount_paid_after_refund_h,
      }))
  },
  scheduledPayments: (state) => {
    return state.payments.scheduledPayments.list
      .map(item => _.pick(item, ['uid', 'amount', 'currency', 'frequency_type', 'name', 'status']))
      .map(item => ({
        uid: item.uid,
        name: item.name,
        amount: item.amount,
        currency: item.currency,
        frequency: item.frequency_type,
        status: item.status,
    }))
  },
  scheduledPaymentsPage: (state)  => state.payments.scheduledPayments.filterBy.page,
  scheduledPaymentsCompleted: (state)  => state.payments.scheduledPayments.completed,
  notYetDuePayments: (state) => {
    return state.payments.notYetDue.list
      .map(item => _.pick(item, ['uid', 'title_h', 'amount_to_pay_h', 'state_summary', 'staff', 'payable_type', 'payable_uid']))
      .map(item => ({
        uid: item.uid,
        title: item.title_h,
        price: item.amount_to_pay_h,
        status: item.state_summary && item.state_summary.state_h.toLocaleLowerCase(),
        payable_type: item.payable_type,
        payable_uid: item.payable_uid,
        staff: item.staff,
      }))
  },
  notYetDuePaymentsPage: (state)  => state.payments.notYetDue.filterBy.page,
  notYetDuePaymentsCompleted: (state)  => state.payments.notYetDue.completed,
  receivedPaymentsPage: (state)  => state.payments.received.filterBy.page,
  receivedPaymentsCompleted: (state)  => state.payments.received.completed,
  estimatesPayments: (state) => {
    return state.payments.estimates.list
    .map(item => _.pick(item, ['uid', 'title_h', 'amount_h', 'state_h', 'staff']))
      .map(item => ({
        uid: item.uid,
        title: item.title_h,
        price: item.amount_h,
        status: item.state_h.toLocaleLowerCase(),
        staff: item.staff
      }))
  },
  estimatesPaymentsPage: (state)  => state.payments.estimates.filterBy.page,
  estimatesPaymentsCompleted: (state)  => state.payments.estimates.completed,
  notes: (state, _getters, rootState) => {
    const staffMembers = rootState.accountModule && rootState.accountModule.staffMembers
    return state.notes.notes.map((note) => {
      return {
        content: note.content,
        uid: note.uid,
        title: note.title,
        staffUid: note.staff_uid,
        staffName: staffNameByUid(note.staff_uid, staffMembers),
        createdAt: note.created_at,
        updatedAt: note.updated_at,
        // Flat v3 entity fields. The `?? note.entity?.x` fallback covers the legacy v1
        // nested shape while rollout.clients.new_notes rolls out.
        // TODO: (VCITA2-14470 — rollout.clients.new_notes cleanup): drop the fallback, read v3 flat only.
        entityUid: note.entity_uid ?? note.entity?.uid ?? null,
        entityType: note.entity_type ?? note.entity?.type ?? null,
        entityTitle: note.entity_title ?? note.entity?.title ?? null,
        // Server-computed capability (v3); gates the Remove action. Legacy v1 sends no
        // capabilities → default true (ungated). `??` preserves an explicit v3 false.
        canDelete: note.capabilities?.delete ?? true,
        page: note.page,
        summary: note.summary,
        summaryStatus: note.summary_status,
      }
    })
  },
  notesPage: (state)  => state.notes.filterBy.page,
  notesCompleted: (state)  => state.notes.completed,
  perPage: () => defaultFilterParams.per_page,
  loadingMatter: (state) => state.loadingMatter,
  bookingSummary: (state) => state.bookingSummary,
  collaborators: state => state.collaborators,
  conversationLatestUpdate: state => state.conversation.latest_update,
  conversationLatestMessageId: state => state.conversation.latest_message_id,
  conversationAllMessagesWasRead: state => state.conversation.all_messages_was_read,
  allDocuforms: state => state.docuforms.all.items,
  allDocuformsCompleted: state => state.docuforms.all.completed,
  allDocuformsError: state => state.docuforms.all.error,
  allDocuformsPage: state => state.docuforms.all.filterBy.page,
  approvedDocuforms: state => state.docuforms.approved.items,
  approvedDocuformsCompleted: state => state.docuforms.approved.completed,
  approvedDocuformsPage: state  => state.docuforms.approved.filterBy.page,
  clientDocuforms: state => state.docuforms.client.items,
  clientDocuformsCompleted: state => state.docuforms.client.completed,
  clientDocuformsPage: state  => state.docuforms.client.filterBy.page,
  businessDocuforms: state => state.docuforms.business.items,
  businessDocuformsCompleted: state => state.docuforms.business.completed,
  businessDocuformsPage: state  => state.docuforms.business.filterBy.page,
  internalDocuforms: state => state.docuforms.internal.items,
  internalDocuformsCompleted: state => state.docuforms.internal.completed,
  internalDocuformsPage: state  => state.docuforms.internal.filterBy.page,
  docuform: state  => state.docuform,
  shouldReloadMatter: state => state.shouldReloadMatter,
  newRecommendationData: state => state.newRecommendationData,
  messageDialogOptions: state => state.messageDialogOptions,
};

const state = getDefaultState()

const mutations = {
  resetState (state) {
    Object.assign(state, getDefaultState())
  },
  setMatter(state, matter) {
    state.matter = matter
  },
  setReminder(state, reminder) {
    Vue.set(state.matter, 'reminder', reminder)
  },
  setCurrContact(state, contact) {
    state.currContact = contact;
  },
  setIsClientOptOutSms(state, isOptOut) {
    state.isClientOptOutSms = isOptOut;
  },
  setPreferredLanguage(state, language) {
    state.preferredLanguage = language;
  },
  setContactFields(state, fields) {
    state.contactFields = fields
  },
  setActiveMatterTab(state, tab) {
    state.activeMatterTab = tab
  },
  setPastBookings(state, bookings) {
    state.bookings.past.list.push(...bookings)
  },
  setPastPage(state, page) {
    state.bookings.past.filterBy.page = page
  },
  setPastBookingsCompleted(state, completed) {
    state.bookings.past.completed = completed
  },
  setUpcomingBookings(state, bookings) {
    state.bookings.upcoming.list.push(...bookings)
  },
  setUpcomingPage(state, page) {
    state.bookings.upcoming.filterBy.page = page
  },
  setUpcomingBookingsCompleted(state, completed) {
    state.bookings.upcoming.completed = completed
  },
  resetBookings(state) {
    state.bookings = {
      past: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      },
      upcoming: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      }
    }
  },

  setMessages(state, messages) {
    state.conversation.messages = _.uniqBy([...state.conversation.messages, ...messages], 'uid');
    state.conversation.latest_update = Math.floor(new Date(state.conversation.messages[0].updated_at)/1000);
  },
  setChannel(state, channel) {
    state.conversation.channel = channel;
  },
  setMessagesCompleted(state, completed) {
    state.conversation.completed = completed
  },
  setMessagesError(state, error) {
    state.conversation.error = error
  },
  setMessagesPage(state, page) {
    state.conversation.filterBy.page = page
  },
  resetConversation(state) {
    state.conversation = {
      filterBy: {...defaultFilterParams},
      completed: false,
      latest_update: 0,
      error: false,
      messages: [],
      channel: EMAIL_CHANNEL_UID,
    }
  },
  setLastUpdateFromTimestamp(state, timestamp) {
    state.conversation.latest_update = parseInt(timestamp)
  },
  updateMessagesWithNewData(state, newData){
    let updatedHash = {}
    newData.reverse().forEach((message)=>{
      let lastMessage = state.conversation.messages[0]
      state.conversation.latest_update = Math.floor(Math.max(new Date(message.updated_at).getTime()/1000, new Date(state.conversation.latest_update).getTime()))

      if (state.conversation.messages.length === 0 || message.created_at > lastMessage.created_at){
        state.conversation.messages.unshift(message)
      } else {
        updatedHash[message.uid] = message
      }
    })

    for (let i = 0; i < state.conversation.messages.length; i++){
      let curUid = state.conversation.messages[i].uid;
      if (updatedHash[curUid]){
        Vue.set(state.conversation.messages, i, updatedHash[curUid])
      }
    }
  },
  addMessage(state, message) {
    state.conversation.messages.unshift(message)
  },

  setDocuments(state, documents) {
    state.documents.docs = documents
  },
  setDocumentsCompleted(state, completed) {
    state.documents.completed = completed
  },
  setDocumentsPage(state, page) {
    state.documents.filterBy.page = page
  },
  resetDocuments(state) {
    state.documents = {
      filterBy: {...defaultFilterParams},
      completed: false,
      docs: [],
    }
  },

  setAllDocuforms(state, docuforms) {
    state.docuforms.all.items.push(...docuforms);
  },
  setAllDocuformsPage(state, page) {
    state.docuforms.all.filterBy.page = page;
  },
  setAllDocuformsCompleted(state, completed) {
    state.docuforms.all.completed = completed;
  },
  setAllDocuformsError(state, error) {
    state.docuforms.all.error = error;
  },

  setApprovedDocuforms(state, docuforms) {
    state.docuforms.approved.items.push(...docuforms)
  },
  setApprovedDocuformsPage(state, page) {
    state.docuforms.approved.filterBy.page = page
  },
  setApprovedDocuformsCompleted(state, completed) {
    state.docuforms.approved.completed = completed
  },

  setClientDocuforms(state, docuforms) {
    state.docuforms.client.items.push(...docuforms)
  },
  setClientDocuformsPage(state, page) {
    state.docuforms.client.filterBy.page = page
  },
  setClientDocuformsCompleted(state, completed) {
    state.docuforms.client.completed = completed
  },

  setBusinessDocuforms(state, docuforms) {
    state.docuforms.business.items.push(...docuforms)
  },
  setBusinessDocuformsPage(state, page) {
    state.docuforms.business.filterBy.page = page
  },
  setBusinessDocuformsCompleted(state, completed) {
    state.docuforms.business.completed = completed
  },

  setInternalDocuforms(state, docuforms) {
    state.docuforms.internal.items.push(...docuforms)
  },
  setInternalDocuformsPage(state, page) {
    state.docuforms.internal.filterBy.page = page
  },
  setInternalDocuformsCompleted(state, completed) {
    state.docuforms.internal.completed = completed
  },

  resetDocuforms(state) {
    state.docuforms = {
      all: {
        filterBy: {...defaultFilterParams},
        completed: false,
        error: false,
        items: []
      },
      approved: {
        filterBy: {...defaultFilterParams},
        completed: false,
        items: []
      },
      client: {
        filterBy: {...defaultFilterParams},
        completed: false,
        items: []
      },
      business: {
        filterBy: {...defaultFilterParams},
        completed: false,
        items: []
      },
      internal: {
        filterBy: {...defaultFilterParams},
        completed: false,
        items: []
      }
    };
  },

  setDocuform(state, item) {
    state.docuform = item
  },

  setPaymentsSummaryData(state, summary) {
    Vue.set(state.payments.summary, 'data', summary)
  },

  setPaymentsSummaryLoading(state, isLoading) {
    Vue.set(state.payments.summary, 'loading', isLoading)
  },

  setPendingInvoices(state, invoices) {
    state.payments.pendingInvoices = invoices;
  },
  setOpenPaymentRequests(state, paymentRequests) {
    const totalAmount = paymentRequests
      .map((item) => Number(item.amount) - Number(item.amount_paid || 0) - Number(item.amount_pending || 0))
      .reduce((a, b) => a + b, 0);
    const currency = paymentRequests[0]?.currency || 'USD';
    state.payments.openPaymentRequests = { totalAmount, currency };
  },

  setOverdueAndDuePaymentsLoader(state, isLoading) {
    Vue.set(state.payments.overdueAndDue, 'loading', isLoading)
  },
  setOverdueAndDuePayments(state, payments) {

    state.payments.overdueAndDue.list.push(...payments)
  },
  setOverdueAndDuePaymentsPage(state, page) {
    state.payments.overdueAndDue.filterBy.page = page
  },
  setOverdueAndDuePaymentsCompleted(state, completed) {
    state.payments.overdueAndDue.completed = completed
  },

  setScheduledPaymentsLoader(state, isLoading) {
    Vue.set(state.payments.scheduledPayments, 'loading', isLoading)
  },
  setScheduledPayments(state, payments) {
    state.payments.scheduledPayments.list = payments
  },
  setScheduledPaymentsNextPage(state) {
    state.payments.scheduledPayments.filterBy.page += 1
  },
  setScheduledPaymentsCompleted(state, completed) {
    state.payments.scheduledPayments.completed = completed
  },
  setNotYetDuePaymentsLoader(state, isLoading) {
    Vue.set(state.payments.notYetDue, 'loading', isLoading)
  },
  setNotYetDuePayments(state, payments) {

    state.payments.notYetDue.list.push(...payments)
  },
  setNotYetDuePaymentsPage(state, page) {
    state.payments.notYetDue.filterBy.page = page
  },
  setNotYetDuePaymentsCompleted(state, completed) {
    state.payments.notYetDue.completed = completed
  },

  setReceivedPayments(state, payments) {
    state.payments.received.list.push(...payments)
  },
  setReceivedPaymentsPage(state, page) {
    state.payments.received.filterBy.page = page
  },
  setReceivedPaymentsCompleted(state, completed) {
    state.payments.received.completed = completed
  },

  setEstimatesPayments(state, payments) {
    state.payments.estimates.list.push(...payments)
  },
  setEstimatesPaymentsPage(state, page) {
    state.payments.estimates.filterBy.page = page
  },
  setEstimatesPaymentsCompleted(state, completed) {
    state.payments.estimates.completed = completed
  },

  resetPayments(state) {
    state.payments = {
      filterBy: {...defaultFilterParams},
      summary: { loading: false, data: null},
      pendingInvoices: [],
      openPaymentRequests: { totalAmount: 0, currency: 'USD' },
      overdueAndDue: {
        filterBy: {...defaultFilterParams},
        completed: false,
        loading: false,
        list: [],
      },
      notYetDue: {
        filterBy: {...defaultFilterParams},
        completed: false,
        loading: false,
        list: [],
      },
      received: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      },
      scheduledPayments: {
        filterBy: {...defaultFilterParams},
        completed: false,
        loading: false,
        list: [],
      },
      estimates: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      }
    }
  },

  setNotes(state, notes) {
    state.notes.notes.push(...notes)
  },
  setNotesCompleted(state, completed) {
    state.notes.completed = completed
  },
  setNotesPage(state, page) {
    state.notes.filterBy.page = page
  },

  resetNotes(state) {
    state.notes = {
      filterBy: {...defaultFilterParams},
      completed: false,
      notes: [],
    }
  },

  removeNote(state, noteId) {
    state.notes.notes = state.notes.notes.filter(n => n.id !== noteId)
  },

  updateNoteSummary(state, { uid, summary, summary_status }) {
    const index = state.notes.notes.findIndex(n => n.uid === uid);
    if (index !== -1) {
      Vue.set(state.notes.notes, index, { ...state.notes.notes[index], summary, summary_status });
    }
    const recentNote = state.summary.recent?.note;
    if (recentNote?.uid === uid) {
      Vue.set(state.summary.recent, 'note', { ...recentNote, summary, summary_status });
    }
  },

  setActivePollingNoteUid(state, noteUid) {
    state.activePollingNoteUid = noteUid;
  },

  setMatterSummaryLoading(state, isLoading) {
    Vue.set(state.summary, 'loading', isLoading)
  },
  setRecentSummary(state, recent) {
    Vue.set(state.summary, 'recent', recent)
  },
  setLoadingMatter(state, loading) {
    state.loadingMatter = loading;
  },
  setBookingSummary(state, bookingSummary) {
    state.bookingSummary = bookingSummary;
  },
  setCollaborators(state, collaborators) {
    state.collaborators = collaborators;
  },
  setCollaboratorsPromise(state, promise) {
    state.collaboratorsPromise = promise;
  },
  setMatterSummaryPromise(state, promise) {
    state.matterSummaryPromise = promise;
  },
  setMatterReload(state, reload) {
    state.shouldReloadMatter = reload;
  },
  setNewRecommendationData(state, data) {
    state.newRecommendationData = data;
  },
  setMessageDialogOptions(state, options) {
    state.messageDialogOptions = {
      showSmartReply: options.showSmartReply || false,
      showCustomInstructions: options.showCustomInstructions || false,
    };
  },
  clearMessageDialogOptions(state) {
    state.messageDialogOptions = {
      showSmartReply: false,
      showCustomInstructions: false,
    };
  }
}

const actions = {
  async fetchMatterData({dispatch}, {payload}) {
    const success = await dispatch({type: "fetchMatter", payload});
    success && await dispatch({type: "fetchContact"});
  },
  async fetchMatter({commit, dispatch, state}, {payload}) {
    const {matterUid, reload} = payload

    try {
      if (reload || !state.matter) {
        commit('setLoadingMatter', true)
        commit('resetBookings')
        commit('resetConversation')
        commit('resetPayments')
        commit('resetDocuments')
        commit('resetDocuforms')
        commit('resetNotes')
        commit('setMatter', null)
      }

      const res = await matterService.get(matterUid)
      commit('setMatter', res.data.matter)
      commit('setLoadingMatter', false)
      commit('setMatterReload', false);
      return res.data
    } catch(e) {
      console.error('error', e);
      commit('setLoadingMatter', false)
    }
  },
  async fetchContact({commit, dispatch, state, rootState}) {
    const {uid} = state.matter.contacts[0];
    const {error, client} = await contactService.getClient(uid);

    if (error) {
      console.error(error);
      commit('setCurrContact', null);
      return;
    }

    if (rootState.clientFields && !rootState.clientFields.clientFields.length) {
      await dispatch('clientFields/fetchClientFields', null, {root: true});
    }

    const {data} = await contactService.getContact(uid);

    if (data && data.contact && rootState.clientFields && rootState.clientFields.clientFields.length) {
      commit('setContactFields', data.contact.fields);

      const {clientFields} = rootState.clientFields;
      client.custom_fields = {};

      data.contact.fields.forEach(field => {
        if (field.value === null) {
          return;
        }

        if (!field.uid) {
          if (clientFields.find(clientField => clientField.label === field.label)) {
            return;
          } else {
            client.custom_fields[field.label] = field.value;
            return;
          }
        }

        const fieldSettings = clientFields.find(clientField => clientField.id === field.uid);

        if (fieldSettings && fieldSettings.custom && fieldSettings.type !== 'phone') {
          client.custom_fields[field.label] = field.value;
        }
      });
    }

    commit('setCurrContact', client);
    dispatch('channelsModule/systemUpdatedChannel', {}, {root:true});
  },
  async fetchClientSettings({commit}) {
    const {uid} = state.matter.contacts[0];
    const result = await contactService.getClientSettings(uid);
    if (result.success && result.data) {
      commit('setIsClientOptOutSms', result.data.opt_out_transactional_sms);
      commit('setPreferredLanguage', result.data.effective_language);
    }
  },
  setLastUpdate({commit}, {payload}) {
    const {timestamp} = payload;
    commit('setLastUpdateFromTimestamp', timestamp);
  },
  saveMatterTab({commit}, {payload}) {
    const {tab} = payload;
    sessionStorage.setItem('activeMatterTab', tab);
    commit('setActiveMatterTab', tab);
  },
  async loadPastBookings({commit, state}, {payload}) {
    const matterUid = state.matter.uid
    const filterBy = {
      ...state.bookings.past.filterBy,
      client: payload.clientId,
      conversation_id: matterUid,
      end_date: new Date(Date.now()).toISOString()
    }
    const {error, bookings} = await bookingsService.list(filterBy)
    if (error) {
      console.error('error', error);
      return
    }

    if (!bookings || bookings.length === 0) {
      commit('setPastBookingsCompleted', true)
      return
    }

    commit('setPastBookings', bookings)
    if (bookings.length < state.bookings.past.filterBy.per_page) {
      commit('setPastBookingsCompleted', true)
    }
  },
  async loadUpcomingBookings({commit, state}, {payload}) {
    const matterUid = state.matter.uid
    const filterBy = {
      ...state.bookings.upcoming.filterBy,
      page: payload.page || state.bookings.upcoming.filterBy.page,
      client: payload.clientId,
      conversation_id: matterUid,
      start_date: new Date(Date.now()).toISOString(),
      sort_order: 'asc'
    }

    const {error, bookings} = await bookingsService.list(filterBy)
    if (error) {
      console.error('error', error);
      return
    }

    if (!bookings || bookings.length === 0) {
      commit('setUpcomingBookingsCompleted', true)
      return
    }

    commit('setUpcomingBookings', bookings)
    if (bookings.length < state.bookings.upcoming.filterBy.per_page) {
      commit('setUpcomingBookingsCompleted', true)
    }
  },

  resetBookings({commit}) {
    commit('resetBookings')
  },
  resetState ({commit}) {
    commit('resetState')
  },
  async loadMessages({commit, state, dispatch}) {
    const params = {
      ...state.conversation.filterBy,
      localized: true,
      full: true
    }

    try {
      let res = await conversationService.getMessages(state.matter.uid, params)
      const messages = res.data
      if(messages.length && state.matter?.uid !== messages[0].conversation_uid) {
        res = await conversationService.getMessages(state.matter.uid, params)
        // matter can be change to null while waiting to "getMessages" response.
        // this can happen by the user that quick click on other matte in inbox page
        if(messages.length && !state.matter || state.matter.uid !== messages[0].conversation_uid) {
          throw new Error('To fast changing matter. Please try again.')
        }
      }
      if (!messages || messages.length === 0) {
        commit('setMessagesCompleted', true)
        return
      }
      commit('setMessages', messages)
      dispatch('channelsModule/systemUpdatedChannel', {}, {root:true});
    }
    catch (error) {
      console.error('error', error);
      commit('setMessagesError', true)
    }
  },

  setChannel({commit}, payload) {
    commit('setChannel', payload)
  },

  async sendMessage({commit, state}, payload) {
    const {error, message} = await conversationService.sendMessage(state.matter.uid, payload.message)

    if (error) {
      console.error('error', error);
      return { error }
    }

    commit('addMessage', message)
    return { message }
  },

  async sendMultipleMessages({commit, state}, payload) {
    const {error} = await conversationService.sendMultipleMessages(payload.message)

    if (error) {
      console.error('error', error);
      return {error}
    }
    return {success: true}
  },

  async updateFollowUp({state, commit}, {payload}) {
    try {
      let matterUid;

      if (payload) {
        matterUid = payload.matterUid ? payload.matterUid : state.matter && state.matter.uid
      }
      if (matterUid) {
        const reminderData = {
          follow_up: true,
          follow_up_date: payload.reminder.date,
          follow_up_message: payload.reminder.message,
        }

        let res = await conversationService.update(matterUid, reminderData)
        if (res?.conversation) {
          commit('setReminder', { date: reminderData.follow_up_date, message: reminderData.follow_up_message })
        }
        return res
      }
    } catch(e) {
      console.error('error:', e.error)
    }
  },

  async updateConversation({state}, {payload}) {
    try {
      if (state.matter && state.matter.uid && payload)
        await conversationService.update(state.matter.uid, payload.data)
    } catch(e) {
      console.error('error:', e.error)
    }
  },

  resetConversation({commit}) {
    commit('resetConversation')
  },
  async fetchNewMessageUpdates({commit}, {payload}) {
    let filter = state.conversation.filterBy

    const params = {
      ...filter,
      last_update: new Date(),
      localized: !payload.getAll,
      full: true
    }

    params.page = 0

    const res = await conversationService.getMessages(state.matter.uid, params)

    if (res.data && res.data.length > 0){
      commit('updateMessagesWithNewData', res.data)
    }
  },
  async loadDocuments({commit, state}, {payload}) {
    const filterBy = {
      ...state.documents.filterBy,
      client: payload.clientId,
      conversation_uid: payload.matterId,
    }
    const {error, documents} = await documentService.list(filterBy)
    if (error) {
      console.error('error', error);
      return
    }

    if (!documents || documents.length === 0) {
      commit('setDocumentsCompleted', true)
      return
    }

    commit('setDocuments', _.uniqBy([...state.documents.docs, ...documents], 'uid'))
    if (documents.length < state.documents.filterBy.per_page) {
      commit('setDocumentsCompleted', true)
    }
  },

  resetDocuments({commit}) {
    commit('resetDocuments')
  },

  async loadDocuforms({commit, state, rootState}, {payload}) {
    const listName = payload.initiator.toLowerCase();
    const capListName = _.capitalize(listName);
    let statusFilter = '';

    switch (payload.initiator) {
      case "approved":
        statusFilter = 'Approved';
        break;
      case "client":
        statusFilter = 'Submitted';
        break;
      case "business":
        statusFilter = 'Reviewed,Pending,Rejected';
        break;
      case "internal":
        statusFilter = 'Internal';
        break;
    }

    const params = {
      ...state.docuforms[listName].filterBy,
      'filter[matter_uid]': state.matter.uid,
      'filter[status][in]': statusFilter
    }

    const {errors, docuforms} = await docuformsService.getFilteredList(params);

    if (errors) {
      console.error('error', errors[0].message);
      return;
    }

    docuforms.forEach(docuform => {
      const staff = rootState.accountModule.staffMembers.find(staff => staff.uid === docuform.staff_uid);

      if (staff) {
        docuform.staff_name = staff.full_name;
      }
    });

    if (!docuforms || docuforms.length === 0) {
      commit(`set${capListName}DocuformsCompleted`, true);
      return;
    }

    commit(`set${capListName}Docuforms`, docuforms);

    if (docuforms.length < state.docuforms[listName].filterBy.per_page) {
      commit(`set${capListName}DocuformsCompleted`, true);
    }
  },

  async loadAllDocuforms({commit, state, rootState}) {
    const {error, docuforms} = await docuformsService.getList(state.docuforms.all.filterBy);

    if (error) {
      console.error('error', error);
      commit('setAllDocuformsError', true);
      return;
    }

    commit('setAllDocuformsError', false);

    if (!docuforms || docuforms.length === 0) {
      commit('setAllDocuformsCompleted', true);
      return;
    }

    const matterUids = _.uniq(docuforms.map(docuform => docuform.matter_uid));
    const res = await Promise.all([matterService.getMultiple(matterUids), matterService.getAvatars(matterUids)]);
    const matters = res[0];
    const avatars = res[1];

    docuforms.forEach(docuform => {
      const staff = rootState.accountModule.staffMembers.find(staff => staff.uid === docuform.staff_uid);
      const matter = matters.data.matters.find(matter => matter.uid === docuform.matter_uid);

      if (staff) {
        docuform.staff_name = staff.full_name;
      }

      if (matter) {
        docuform.matter = matter;
        docuform.contact = matter.contacts[0];
        docuform.contact.full_name = docuform.contact.display_name;

        const avatar = avatars.data.avatars.find(avatar => avatar.matter_uid === docuform.matter.uid);

        if (avatar) {
          docuform.contact.photo_path = avatar.photo;
          docuform.contact.color_id = avatar.color;
        }
      }
    });

    commit('setAllDocuforms', docuforms);

    if (docuforms.length < state.docuforms.all.filterBy.per_page) {
      commit('setAllDocuformsCompleted', true);
    }
  },

  resetDocuforms({commit}) {
    commit('resetDocuforms')
  },

  async loadDocuform({commit, state, rootState}, {payload}) {
    const docuform = state.docuforms.all.items.find(doc => doc.uid === payload.uid);

    if (docuform) {
      commit('setDocuform', docuform);
      return;
    }

    const item = await docuformsService.getActivity(payload.uid);
    const staff = rootState.accountModule.staffMembers.find(staff => staff.uid === item.staff_uid);

    if (staff) {
      item.staff_name = staff.full_name;
    }

    return commit('setDocuform', item);
  },

  async loadPaymentsSummary({commit, state}) {
    // todo: use as promise and prevent double call for the server
    const clientUid = state.matter.contacts[0].uid
    const filterBy = {id: clientUid, conversation_id: state.matter.uid, exclude_items:true}

    if (state.payments.summary.loading) return

    commit('setPaymentsSummaryLoading', true)
    try {
      const res = await paymentsService.getSummary(filterBy);
      commit('setPaymentsSummaryData', res.data.balances)

      const filterPendingInvoices = {
        per_page: 1,
        client: clientUid,
        conversation_id: state.matter.uid,
        by_status: "past_due,outstanding",
        by_type: "'Invoice'",
        sort_by: 'created_at',
      }
      const pendingInvoices = await paymentsStatusesService.list(filterPendingInvoices);
      commit('setPendingInvoices', pendingInvoices.data);

      const openPaymentRequests = await paymentsStatusesService.fetchOpenPaymentRequests(state.matter.uid);
      commit('setOpenPaymentRequests', openPaymentRequests);

    } catch(e) {
      console.error('error', e);
    }
    commit('setPaymentsSummaryLoading', false)
  },
  async refreshScheduledPayments({dispatch, commit, state, rootGetters}, payload) {
    commit('setScheduledPaymentsLoader', true)

    await dispatch('paymentsModule/fetchScheduledPayments', payload.scheduledPaymentUid, {root: true});
    const scheduledPayments = rootGetters['paymentsModule/engagementScheduledPayments'](state.matter.uid).reverse();
    commit('setScheduledPayments', scheduledPayments)

    commit('setScheduledPaymentsLoader', false)
  },
  async loadScheduledPayments({dispatch, commit, state, rootGetters}) {
    const matterUid = state.matter.uid
    const filterBy = {
      ...state.payments.scheduledPayments.filterBy,
      'filter[engagement_uid]': matterUid,
    }
    try {
      commit('setScheduledPaymentsLoader', true)
      const filteredScheduledPayments = await dispatch('paymentsModule/fetchFilteredScheduledPayments', filterBy, {root: true});

      if (filteredScheduledPayments.scheduled_payments_rules.length === state.payments.scheduledPayments.filterBy.per_page) {
        commit('setScheduledPaymentsNextPage')
      } else {
        const scheduledPayments = rootGetters['paymentsModule/engagementScheduledPayments'](matterUid).reverse();
        commit('setScheduledPayments', scheduledPayments)
        commit('setScheduledPaymentsLoader', false)
        commit('setScheduledPaymentsCompleted', true)
      }
    } catch(e) {
      commit('setScheduledPaymentsLoader', false)
      commit('setScheduledPaymentsCompleted', true)
      console.error('error', e);
    }
  },
  async loadOverdueAndDuePayments({commit, state}) {
    const clientUid = state.matter.contacts[0].uid
    const matterUid = state.matter.uid
    const filterBy = {
      ...state.payments.overdueAndDue.filterBy,
      client: clientUid,

      conversation_id: matterUid,
      by_status: "past_due,outstanding",
      sort_by: 'created_at',
      has_invoice: 'false',
      full: true,
    }

    try {
      commit('setOverdueAndDuePaymentsLoader', true)
      const res = await paymentsStatusesService.list(filterBy)
      const data = (res.data || []).sort((a,b) => (a.state > b.state) ? 1 : ((b.state > a.state) ? -1 : 0));
      commit('setOverdueAndDuePayments', data)
      if (data.length < state.payments.overdueAndDue.filterBy.per_page) {
        commit('setOverdueAndDuePaymentsCompleted', true)
      }
    } catch(e) {
      console.error('error', e);
    }

    commit('setOverdueAndDuePaymentsLoader', false)
  },

  async loadNotYetDuePayments({commit, state}) {
    const clientUid = state.matter.contacts[0].uid
    const matterUid = state.matter.uid
    const filterBy = {
      ...state.payments.notYetDue.filterBy,
      client: clientUid,

      conversation_id: matterUid,
      by_status: "not_yet_due",
      sort_by: 'created_at',
      has_invoice: 'false',
      full: true,
    }

    try {
      commit('setNotYetDuePaymentsLoader', true)
      const res = await paymentsStatusesService.list(filterBy)
      const data = res.data || []
      commit('setNotYetDuePayments', data)
      if (data.length < state.payments.notYetDue.filterBy.per_page) {
        commit('setNotYetDuePaymentsCompleted', true)
      }
    } catch(e) {
      console.error('error', e);
    }

    commit('setNotYetDuePaymentsLoader', false)
  },

  async loadReceivedPayments({commit, state}) {
    const clientUid = state.matter.contacts[0].uid
    const matterUid = state.matter.uid
    const filterBy = {
      ...state.payments.received.filterBy,
      client: clientUid,
      conversation_id: matterUid,
      by_payment_method: 'all',
      by_status: 'paid,partially_refunded,refunded',
      sort_by: 'paid_at',
      localized: true,
      payment_list: true
    }

    try {
      const res = await paymentsService.list(filterBy);
      const data = res.data || []
      commit('setReceivedPayments', data)
      if (data.length < state.payments.received.filterBy.per_page) {
        commit('setReceivedPaymentsCompleted', true)
      }
    } catch(e) {
      console.error('error', e);
    }
  },

  async loadEstimatesPayments({commit, state}) {
    const clientUid = state.matter.contacts[0].uid
    const matterUid = state.matter.uid
    const filterBy = {
      ...state.payments.estimates.filterBy,
      client: clientUid,
      conversation_id: matterUid,
      has_invoice: false,
      by_payment_method: 'all',
      by_status: 'issued,draft,approved,invoiced',
      sort_by: 'created_at',
      full: true,
    }

    try {
      const res = await estimateService.list(filterBy)
      const data = res.data || []
      commit('setEstimatesPayments', data)
      if (data.length < state.payments.estimates.filterBy.per_page) {
        commit('setEstimatesPaymentsCompleted', true)
      }
    } catch (e) {
      console.error('error', e);
    }

  },

  resetPayments({commit}) {
    commit('resetPayments')
  },

  async loadNotes({commit, state, rootGetters}, {payload}) {
    const useV3 = rootGetters.hasFeature('rollout.clients.new_notes')
    const filterBy = {
      ...state.notes.filterBy
    }

    let notes
    if (useV3) {
      try {
        // Same page/per_page as v1 — both endpoints share the backend pagination helper.
        const {data} = await notesService.listV3(payload.matterId, {page: filterBy.page, per_page: filterBy.per_page})
        notes = data?.client_notes || []
      } catch (error) {
        this.$logError('LOAD-NOTES-V3-ERROR', {error, matterId: payload.matterId, filterBy});
        return;
      }
    } else {
      const {errors, data} = await notesService.list(payload.matterId, filterBy)
      if (errors) {
        console.error(errors[0].message);
        return;
      }
      notes = data.notes
    }

    if (!notes || notes.length === 0) {
      commit('setNotesCompleted', true)
      return
    }

    notes.forEach(note => note.page = state.notes.filterBy.page)

    commit('setNotes', notes)
    if (notes.length < state.notes.filterBy.per_page) {
      commit('setNotesCompleted', true)
    }
  },

  resetNotes({commit}) {
    commit('resetNotes')
  },

  async removeNote({commit, state, rootGetters}, {payload}) {
    const noteId = payload.id
    const useV3 = rootGetters.hasFeature('rollout.clients.new_notes')

    try {
      if (useV3) {
        // removeV3 rejects on failure; the v1 remove resolves with {error} — normalize
        // both to a throw so the shared catch handles them the same way.
        await notesService.removeV3(noteId)
      } else {
        const {error} = await notesService.remove(state.matter.uid, noteId)
        if (error) throw error
      }
    } catch (error) {
      this.$logError('REMOVE-NOTE-ERROR', {error, noteId});
      return;
    }

    commit('removeNote', noteId)
  },

  // To minimize backend calls when several notes are awaiting summaries, we only actively poll the
  // saved note. The other in-progress notes (pendingSummaryNoteUids) are refreshed with a single
  // fetch once the polled note's summary arrives.
  async pollNotesSummary({ commit, dispatch, state, getters }, { noteUid, summaryExpectedAt }) {
    if (!noteUid || !summaryExpectedAt) return;
    const polledMatterUid = state.matter?.uid;
    commit('setActivePollingNoteUid', noteUid);
    const isMatterChanged = () => state.matter?.uid !== polledMatterUid;
    const isCancelled = () => isMatterChanged() || state.activePollingNoteUid !== noteUid;

    const note = await pollNoteUntilSummaryReady({ noteUid, summaryExpectedAt, isCancelled });
    if (!note || isCancelled()) return;
    commit('updateNoteSummary', { uid: note.uid, summary: note.summary, summary_status: note.summary_status });

    getters.pendingSummaryNoteUids(noteUid).forEach(uid => dispatch('fetchAndUpdatePendingNoteSummary', { noteUid: uid, isMatterChanged }));
  },

  async fetchAndUpdatePendingNoteSummary({ commit }, { noteUid, isMatterChanged }) {
    if (isMatterChanged()) return;
    const note = await fetchNoteV3(noteUid);
    if (!note || isMatterChanged()) return;
    commit('updateNoteSummary', { uid: note.uid, summary: note.summary, summary_status: note.summary_status });
  },

  cancelNotePolling({ commit }) {
    commit('setActivePollingNoteUid', null);
  },

  async loadMatterSummary({commit, state}, payload) {
    if ((!payload || !payload.refresh) && state.matterSummaryPromise) {
      return state.matterSummaryPromise;
    }

    commit('setMatterSummaryLoading', true);

    const promise = matterService.summary(state.matter.uid).then(res => {
      if (res.data.summary) {
        commit('setRecentSummary', res.data.summary.recent);
        commit('setBookingSummary', res.data.summary.booking);
      }
    }).catch(error => {
      console.error("loadMatterSummary", error);
    }).finally(() => {
      commit('setMatterSummaryLoading', false);
    });

    commit('setMatterSummaryPromise', promise);
    return promise;
  },

  async refreshMatterSummary({dispatch}) {
    await dispatch({type: "loadMatterSummary", refresh: true});
  },

  async fetchCollaborators({commit, state}, payload) {
    if ((!payload || !payload.refresh) && state.collaboratorsPromise) {
      return state.collaboratorsPromise;
    }

    const promise = staffService.getCollaborators(state.matter.uid).then(collaborators => {
      commit('setCollaborators', collaborators);
    }, error => {
      console.error("fetchCollaborators", error);
    });

    commit('setCollaboratorsPromise', promise);
    return promise;
  },

  async refreshCollaborators({dispatch}) {
    await dispatch({type: "fetchCollaborators", refresh: true});
  },

  setMatterReload({commit}) {
    commit('setMatterReload', true);
  },
  async validatePhone({commit, state}, payload) {
    const response = await contactService.validatePhone(payload.phone);
    return !!response.data;
  },
  async validateEmail({commit, state}, payload) {
    const response = await contactService.validateEmail(payload.email);
    return !!response.data;
  },
  async updateContact({commit, state}, payload) {
    return await contactService.updateContact(payload.contactUid, payload.data);
  },
  async sendCardRequest({commit, state}, payload) {
    return await contactService.sendCardRequest(payload);
  },
  setNewRecommendationData({commit}, value) {
    commit('setNewRecommendationData', value);
  },
  setMessageDialogOptions({commit}, options) {
    commit('setMessageDialogOptions', options);
  },
  clearMessageDialogOptions({commit}) {
    commit('clearMessageDialogOptions');
  }
}


export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
